import React from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import type { Transaction } from '@mobi/api-types/src/account/transactions'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { getOrdinalSuffix } from '@mobi/utils/number/ordinal'

export const DATE_HEADER_DATA_SELECTOR = 'data-js-account-activity-date-header'

type DateHeaderProps = {
  latestDateRef: React.MutableRefObject<string | null>
} & {
  timestamp: Transaction['timestamp']
}

export const DateSubheader: React.FC<DateHeaderProps> = ({ latestDateRef, timestamp }) => {
  const transactionDate = timestamp

  const { dayOfWeek, dateLabel, isoDate, transactionDisplayDate } =
    getDateHeaderParts(transactionDate)

  const shouldHideDate = latestDateRef.current === transactionDisplayDate

  if (!latestDateRef.current || latestDateRef.current !== transactionDisplayDate) {
    latestDateRef.current = transactionDisplayDate
  }

  const dateDataSelectorWithValue = shouldHideDate ? {} : { [DATE_HEADER_DATA_SELECTOR]: isoDate }

  return (
    <div
      data-tid-date-header
      style={shouldHideDate ? { display: 'none' } : { paddingTop: spacing.smx2 }}
    >
      <DateHeaderLabelStyled>
        <span>{dayOfWeek}</span>
        <span data-testid='date-value' {...dateDataSelectorWithValue}>
          {dateLabel}
        </span>
      </DateHeaderLabelStyled>
    </div>
  )
}

const DateHeaderLabelStyled = styled.div({
  display: 'flex',
  fontFamily: font.family.primary,
  fontSize: font.size.lg.fontSize,
  letterSpacing: font.size.lg.letterSpacing,
  lineHeight: font.size.lg.lineHeight,
  fontWeight: font.weight.medium,
  color: colors.neutral[900],
  gap: spacing.smx2,

  '& > span:first-of-type': {
    fontWeight: font.weight.semibold,
    color: colors.lavender[900],
  },

  '& > span:nth-of-type(2)': {
    fontWeight: font.weight.medium,
    color: colors.neutral[800],
  },
})

function getDateHeaderParts(transactionDate?: string | null) {
  let transactionDisplayDate = ''
  let dayOfWeek = ''
  let dateLabel = ''
  let isoDate = ''

  if (dayjs(transactionDate).isValid()) {
    const d = dayjs(transactionDate)
    if (d.isSame(dayjs(), 'day')) {
      dayOfWeek = 'Today'
    } else if (d.isSame(dayjs().subtract(1, 'day'), 'day')) {
      dayOfWeek = 'Yesterday'
    } else {
      dayOfWeek = d.format('dddd')
    }
    const dayNum = d.date()
    const month = d.format('MMM')
    const year = d.format('YYYY')
    dateLabel = `${dayNum}${getOrdinalSuffix(dayNum)} ${month} ${year}`
    transactionDisplayDate = `${dayOfWeek} ${dateLabel}`
    isoDate = d.format('YYYY-MM-DD')
  }

  return { dayOfWeek, dateLabel, isoDate, transactionDisplayDate }
}
